import React from "react";
import { Tooltip } from "@mui/material";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import { AvatarStack, CountUp, ProgressRing, ProjectMark, SparkBars, StackBar } from "./charts";
import { HEALTH_HELP, daysLeftLabel, toSegments } from "./reportModel";

export const HealthPill = ({ view }) => (
  <Tooltip title={HEALTH_HELP}>
    <span className={`pr-health pr-tone--${view.healthMeta.tone}`}>
      <span className="pr-health__dot" />
      {view.healthMeta.label}
    </span>
  </Tooltip>
);

const TrendIcon = ({ trend }) => {
  if (trend > 0) return <TrendingUpRoundedIcon className="pr-trend-icon is-up" titleAccess="Improving" />;
  if (trend < 0) return <TrendingDownRoundedIcon className="pr-trend-icon is-down" titleAccess="Slipping" />;
  return <TrendingFlatRoundedIcon className="pr-trend-icon" titleAccess="Steady" />;
};

const dayTone = (days) => (days < 0 ? "is-red" : days <= 2 ? "is-amber" : "");

const subtitleFor = (view) =>
  [view.key, view.lead?.name ? `Lead: ${view.lead.name}` : null].filter(Boolean).join(" · ") ||
  `${view.team.length} ${view.team.length === 1 ? "member" : "members"}`;

export const ProjectTile = ({ view, index = 0, onOpen }) => {
  const { focus, active } = view;
  const segments = toSegments(view.sprintSummary?.byStatus || (active ? {} : null), "status");

  return (
    <article className="pr-tile" style={{ "--d": `${Math.min(index, 8) * 60}ms`, "--c": view.color }} aria-label={view.name}>
      <header className="pr-tile__head">
        <ProjectMark name={view.name} color={view.color} />
        <div className="pr-tile__title">
          <h3>{view.name}</h3>
          <span>{subtitleFor(view)}</span>
        </div>
        <HealthPill view={view} />
      </header>

      <div className="pr-tile__body">
        <ProgressRing value={view.completion} color={view.color} sublabel={active ? "sprint done" : focus ? "last sprint" : "no sprint"} />
        <div className="pr-tile__sprint">
          <span className="pr-eyebrow">{active ? "Current sprint" : focus ? "Last sprint" : "Sprints"}</span>
          <strong title={focus?.name}>{focus?.name || "No sprints yet"}</strong>
          <span className="pr-muted">{focus ? `${focus.done} of ${focus.total} issues done` : "Start a sprint to see progress"}</span>
          {active && view.daysLeft !== null ? (
            <span className={`pr-chip ${dayTone(view.daysLeft)}`}>
              <ScheduleRoundedIcon />
              {daysLeftLabel(view.daysLeft)}
            </span>
          ) : null}
        </div>
      </div>

      {segments.length ? (
        <div className="pr-tile__status">
          <StackBar segments={segments} />
          <div className="pr-inline-legend">
            {segments.slice(0, 4).map((segment) => (
              <span key={segment.label}>
                <i style={{ background: segment.color }} />
                {segment.label} <b>{segment.value}</b>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <dl className="pr-stats">
        <div>
          <dt>Open</dt>
          <dd>
            <CountUp value={view.issues.open} />
          </dd>
        </div>
        <div>
          <dt>Backlog</dt>
          <dd>{view.issues.backlog === null ? "—" : <CountUp value={view.issues.backlog} />}</dd>
        </div>
        <div>
          <dt>Velocity</dt>
          <dd className="pr-stats__velocity">
            {view.velocity === null ? "—" : view.velocity}
            {view.velocity === null ? null : <TrendIcon trend={view.trend} />}
          </dd>
        </div>
        <div>
          <dt>Points</dt>
          <dd>
            {view.issues.storyPoints.total ? (
              <>
                <CountUp value={view.issues.storyPoints.done} />
                <small>/{view.issues.storyPoints.total}</small>
              </>
            ) : (
              "—"
            )}
          </dd>
        </div>
      </dl>

      <div className="pr-tile__trend">
        <span className="pr-eyebrow">Last {view.history.length || 0} sprints</span>
        <SparkBars history={view.history} color={view.color} />
      </div>

      <footer className="pr-tile__foot">
        <AvatarStack people={view.team} />
        <button type="button" className="pr-link" onClick={() => onOpen(view)} aria-label={`View details for ${view.name}`}>
          View details
          <ArrowForwardRoundedIcon />
        </button>
      </footer>
    </article>
  );
};

export const ProjectList = ({ views = [], onOpen }) => (
  <div className="pr-list" role="table" aria-label="Projects">
    <div className="pr-list__head" role="row">
      <span role="columnheader">Project</span>
      <span role="columnheader">Health</span>
      <span role="columnheader">Sprint</span>
      <span role="columnheader">Progress</span>
      <span role="columnheader" className="pr-num">Open</span>
      <span role="columnheader" className="pr-num">Backlog</span>
      <span role="columnheader">Team</span>
      <span role="columnheader">Last sprints</span>
      <span role="columnheader" aria-label="Open details" />
    </div>
    {views.map((view, index) => (
      <div
        key={view.id}
        className="pr-list__row"
        role="row"
        tabIndex={0}
        aria-label={`${view.name}, ${view.healthMeta.label}, ${view.completion}% complete`}
        style={{ "--d": `${Math.min(index, 10) * 40}ms`, "--c": view.color }}
        onClick={() => onOpen(view)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen(view);
          }
        }}
      >
        <span role="cell" className="pr-list__project">
          <ProjectMark name={view.name} color={view.color} size="sm" />
          <span>
            <strong>{view.name}</strong>
            <small>{subtitleFor(view)}</small>
          </span>
        </span>
        <span role="cell">
          <HealthPill view={view} />
        </span>
        <span role="cell" className="pr-list__sprint">
          <strong>{view.focus?.name || "—"}</strong>
          <small className={view.active ? dayTone(view.daysLeft ?? 99) : ""}>
            {view.active ? daysLeftLabel(view.daysLeft) || "Active" : view.focus ? "Completed" : "No sprints"}
          </small>
        </span>
        <span role="cell" className="pr-list__progress">
          <span className="pr-bar">
            <span style={{ width: `${view.completion}%`, background: view.color }} />
          </span>
          <strong>{view.completion}%</strong>
        </span>
        <span role="cell" className="pr-num">
          {view.issues.open}
        </span>
        <span role="cell" className="pr-num">
          {view.issues.backlog === null ? "—" : view.issues.backlog}
        </span>
        <span role="cell">
          <AvatarStack people={view.team} max={3} size={26} />
        </span>
        <span role="cell">
          <SparkBars history={view.history} color={view.color} height={28} />
        </span>
        <span role="cell" className="pr-list__chevron">
          <ChevronRightRoundedIcon />
        </span>
      </div>
    ))}
  </div>
);
